/**
 * Workflow return-context — the URL convention that lets a detail
 * page know where the operator came from, so closing or completing
 * the entity can drop them back into the exact queue/page/filter
 * state they left behind.
 *
 * Encoded as a single `from` query param on outgoing links:
 *   /issues/BIZ-12?from=<base64url(JSON{ label, href })>
 *
 * The href intentionally carries the source's full search string,
 * so list filters/pagination/selection round-trip without server
 * coordination. A label ("Inbox", "Issues queue") is rendered as
 * the leftmost breadcrumb crumb on the destination.
 *
 * Stays in `lib/` (not `components/`) so server components, route
 * handlers and client components can share the same shape.
 */

/** One navigable ancestor of the source surface. */
export type ReturnCrumb = { label: string; href: string };

export type ReturnContext = {
  /** Short label for the source ("Inbox", "Issues", "Patches"). */
  label: string;
  /** Full destination URL incl. search/hash (e.g. "/issues?status=ready&page=3&selected=BIZ-12"). */
  href: string;
  /**
   * What sat ABOVE the source, when the source is not a top-level surface.
   * A patch opened from a project's patch section came from
   * `Projects › Acme › Patches`, and a single crumb can only say "Patches" —
   * which reads as the tenant-wide queue the operator was not in.
   *
   * Display + navigation only; every crumb is re-authorised by its own route.
   */
  trail?: ReturnCrumb[];
};

/** Ancestors + the source itself, as the head of a breadcrumb trail. */
export function returnCrumbs(ctx: ReturnContext): ReturnCrumb[] {
  return [...(ctx.trail ?? []), { label: ctx.label, href: ctx.href }];
}

/**
 * An internal path, and nothing that leaves this origin.
 *
 * "starts with /" is not enough on its own: `//evil.example` and `/\evil`
 * are protocol-relative URLs that browsers resolve to ANOTHER HOST, and the
 * value arrives in a query param anyone can hand-edit.
 */
function isInternalPath(href: string): boolean {
  return href.startsWith("/") && !href.startsWith("//") && !href.startsWith("/\\");
}

/** A crumb is renderable only if it is text plus an INTERNAL path — the
 *  breadcrumb is a navigation primitive, never an escape to another origin. */
function parseCrumb(value: unknown): ReturnCrumb | null {
  if (!value || typeof value !== "object") return null;
  const { label, href } = value as { label?: unknown; href?: unknown };
  if (typeof label !== "string" || typeof href !== "string") return null;
  if (!isInternalPath(href)) return null;
  // Cap the label: it lands in a fixed-width bar, and the value travels in a
  // URL anyone can hand-edit.
  return { label: label.slice(0, 80), href };
}

const PARAM = "from" as const;

function b64urlEncode(s: string): string {
  if (typeof window === "undefined") {
    return Buffer.from(s, "utf8").toString("base64").replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
  }
  // btoa needs binary string — encodeURIComponent + escape avoids unicode-byte issues.
  return btoa(unescape(encodeURIComponent(s))).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function b64urlDecode(s: string): string | null {
  try {
    const padded = s.replace(/-/g, "+").replace(/_/g, "/") + "===".slice((s.length + 3) % 4);
    if (typeof window === "undefined") {
      return Buffer.from(padded, "base64").toString("utf8");
    }
    return decodeURIComponent(escape(atob(padded)));
  } catch {
    return null;
  }
}

export function encodeReturnContext(ctx: ReturnContext): string {
  return b64urlEncode(JSON.stringify(ctx));
}

export function decodeReturnContext(raw: string | null | undefined): ReturnContext | null {
  if (!raw) return null;
  const json = b64urlDecode(raw);
  if (!json) return null;
  try {
    const parsed = JSON.parse(json);
    if (
      parsed &&
      typeof parsed.label === "string" &&
      typeof parsed.href === "string" &&
      // Reject absolute URLs to other origins — the breadcrumb is
      // an internal-only navigation primitive.
      isInternalPath(parsed.href)
    ) {
      // A malformed ancestor drops the ancestors, never the context itself:
      // the return link is what the operator needs, the trail is decoration.
      const trail = Array.isArray(parsed.trail)
        ? parsed.trail.slice(0, 4).map(parseCrumb)
        : [];
      const crumbs = trail.every((c: ReturnCrumb | null) => c !== null)
        ? (trail as ReturnCrumb[])
        : [];
      return crumbs.length
        ? { label: parsed.label, href: parsed.href, trail: crumbs }
        : { label: parsed.label, href: parsed.href };
    }
  } catch {
    // fall through
  }
  return null;
}

/**
 * Append `?from=<encoded>` to an outgoing href so the destination
 * can render a return-aware breadcrumb. Preserves any existing
 * query string on the target URL.
 */
export function withReturn(href: string, ctx: ReturnContext): string {
  const sep = href.includes("?") ? "&" : "?";
  return `${href}${sep}${PARAM}=${encodeReturnContext(ctx)}`;
}

/** Build a ReturnContext for the *current* page (client only). */
export function currentReturnContext(label: string): ReturnContext | null {
  if (typeof window === "undefined") return null;
  return { label, href: window.location.pathname + window.location.search };
}

export const RETURN_PARAM = PARAM;
